import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtDate } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Eye, Pencil, Search, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const empty = { nome: "", cpf: "", rg: "", nascimento: "", estado_civil: "", telefone: "", telefone2: "",
  whatsapp: "", email: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "",
  estado: "", vendedor_id: "", gerente_id: "", observacoes: "" };

export default function Clients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [sellers, setSellers] = useState([]);

  const load = async () => {
    const { data } = await api.get("/clients", { params: { search } });
    setItems(data);
  };
  useEffect(() => { load(); api.get("/sellers").then(r => setSellers(r.data || [])); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Cliente atualizado" : "Cliente criado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const remove = async (c) => {
    if (!window.confirm(`Excluir cliente "${c.nome}"? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Cliente excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Layout title="Clientes" subtitle="Gestão completa de clientes">
      <Card className="p-4 rounded-sm mb-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="client-search" placeholder="Buscar por nome, CPF ou e-mail..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" />
            </div>
            <Button variant="outline" onClick={load}>Buscar</Button>
          </div>
          {can(user, "manage_clients") && (
            <Button data-testid="client-new" onClick={openNew}><Plus className="w-4 h-4 mr-1"/>Novo Cliente</Button>
          )}
        </div>
      </Card>

      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60">
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Cidade/UF</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Cadastro</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>{c.cpf || "—"}</TableCell>
                <TableCell>{c.cidade ? `${c.cidade}/${c.estado}` : "—"}</TableCell>
                <TableCell>{c.telefone || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Link to={`/clientes/${c.id}`}>
                    <Button data-testid={`client-view-${c.id}`} variant="ghost" size="sm"><Eye className="w-4 h-4"/></Button>
                  </Link>
                  {can(user, "edit_clients") && (
                    <Button data-testid={`client-edit-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="w-4 h-4"/></Button>
                  )}
                  {can(user, "delete_clients") && (
                    <Button data-testid={`client-delete-${c.id}`} variant="ghost" size="sm" onClick={() => remove(c)}><Trash2 className="w-4 h-4"/></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum cliente</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar Cliente" : "Novo Cliente"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            {[
              ["nome", "Nome completo", 3], ["cpf", "CPF"], ["rg", "RG"], ["nascimento", "Nascimento", 1, "date"],
              ["estado_civil", "Estado civil"], ["telefone", "Telefone"], ["telefone2", "Telefone 2"], ["whatsapp", "WhatsApp"],
              ["email", "E-mail", 2, "email"], ["cep", "CEP"], ["endereco", "Endereço", 2], ["numero", "Número"],
              ["complemento", "Complemento", 2], ["bairro", "Bairro"], ["cidade", "Cidade"], ["estado", "UF"],
            ].map(([key, lbl, span = 1, type = "text"]) => (
              <div key={key} className={`md:col-span-${span}`}>
                <Label className="text-xs font-semibold">{lbl}</Label>
                <Input data-testid={`client-${key}`} type={type} value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" />
              </div>
            ))}
            <div className="md:col-span-3">
              <Label className="text-xs font-semibold">Vendedor responsável</Label>
              <SearchableSelect
                value={form.vendedor_id || ""}
                onValueChange={(v) => setForm({ ...form, vendedor_id: v })}
                options={sellers.map(s => ({ value: s.id, label: s.nome }))}
                placeholder="Selecionar..."
                searchPlaceholder="Buscar vendedor..."
                testid="client-vendedor"
                className="mt-1"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs font-semibold">Observações</Label>
              <Input value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} data-testid="client-save">{editing ? "Salvar" : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
